import { Component, OnInit, ViewChild } from '@angular/core';
import { NzDrawerRef, NzMessageService } from 'ng-zorro-antd';
import { Location, LocationTypeName } from '@app/_models/_DTOs/location';
import { Department } from '@app/_models/_DTOs/department';
import { Supplier } from '@app/_models/_DTOs/supplier';
import { AssetHistoryActionType } from '@app/_models/assetHistoryActionType';
import { AssetType } from '@app/_models/enums/assetType';
import { AssetService, AuthenticationService } from '@app/_services';
import { LocationService } from '@app/_services/location.service';
import { DepartmentService } from '@app/_services/department.service';
import { SupplierService } from '@app/_services/supplier.service';
import { environment } from '@environments/environment';
import { NgForm } from '@angular/forms';
import { Asset, AssetTypeName } from '@app/_models/_DTOs/asset';
import { ITuple } from '@app/_models/tuple';
import { AssetHistoryActionName } from '@app/_models/_DTOs/assetHistory';
import { Subject } from 'rxjs';
import { EmployeeService } from '@app/_services/employee.service';
import { Employee } from '@app/_models/_DTOs/employee';
import { LocationViewModel } from '@app/_models/_viewModels/location.viewmodel';
import { LocationType } from '@app/_models/enums/locationType';
import { LegalEntity } from '@app/_models/_DTOs/legalEntity';
import { LegalEntityService } from '@app/_services/legalEntity.service';
import { BaseComponent } from '@app/_shared/base.component';
import { SCREEN_SIZE } from '@app/_helpers/screen-size.enum';
import { UserToken } from '@app/_models';
import { ResizeService } from '@app/_services/resize.service';

@Component({
  selector: "app-locations",
  templateUrl: "./locations.component.html",
  styleUrls: ["./locations.component.css"],
})
export class LocationsComponent extends BaseComponent implements OnInit {
  onScreenResize(screenSize: SCREEN_SIZE) {}
  onUserAvailable(userToken: UserToken) {}
  level: number = 0;
  @ViewChild("detailsDrawer", { static: false }) detailsDrawer: NzDrawerRef;
  @ViewChild("editDrawer", { static: false }) editDrawer: NzDrawerRef;

  pageIndex = 1;
  pageSize = 10;
  total = 1;
  locationList: Location[] = [];
  loading = true;

  loadingDropdowns = false;
  detailsDrawerVisible: boolean = false;
  editDrawerVisible: boolean = false;
  isSavingLocation: boolean = false;
  isEditing: boolean = false;

  employees: Employee[] = [];
  legalEntities: LegalEntity[] = [];
  selectedEmployee: Employee;

  nameSearchValue = "";
  employeeIdSearchValue: number[] = [];
  legalEntityIdSearchValue: number[] = [];
  typeSearchValue: number[] = [];

  selectedLocation: Location;
  selectedLocationAssetsSubject: Subject<ITuple<Asset>> = new Subject<
    ITuple<Asset>
  >();
  locationViewModel: LocationViewModel = null;

  public assetHistoryActionType = AssetHistoryActionType;
  public assetTypes = AssetType;
  public locationTypes = LocationType;

  currencyFormatter = (value: number) => `${value} EUR`;
  currencyParser = (value: string) => value.replace(" EUR", "");

  errorMessages = {};

  constructor(
    private employeeService: EmployeeService,
    private assetService: AssetService,
    private locationService: LocationService,
    private messageService: NzMessageService,
    private legalEntityService: LegalEntityService,
    protected authenticationService: AuthenticationService,
    protected resizeService: ResizeService
  ) {
    super(authenticationService, resizeService);
    this.initModel();
  }

  ngOnInit() {
    this.searchData();
    this.loadDataForDropdowns();
  }

  initModel(location?: Location) {
    if (location) {
      this.locationViewModel = {
        id: location.id ? location.id : -1,
        type: location.type ? location.type : undefined,
        name: location.name ? location.name : "",
        description: location.description ? location.description : "",
        isDefaultForUnassigned: location.isDefaultForUnassigned,
        personInChargeId: location.personInCharge
          ? location.personInCharge.id
          : undefined,
        legalEntityId: location.legalEntity
          ? location.legalEntity.id
          : undefined,
      };
    } else {
      this.locationViewModel = {
        id: -1,
        type: undefined,
        name: "",
        description: "",
        isDefaultForUnassigned: false,
        personInChargeId: undefined,
        legalEntityId: undefined,
      };
    }
  }

  saveLocation(form: NgForm) {
    this.isSavingLocation = true;
    if (this.isEditing) {
      this.locationService
        .put(this.locationViewModel, this.locationViewModel.id)
        .subscribe(
          (result) => {
            this.messageService.create(
              "success",
              "Lokacija uspješno izmijenjena",
              {
                nzDuration: 3000,
              }
            );
            this.isSavingLocation = false;
            this.searchData();
            form.reset();
            this.closeEditDrawer();
          },
          (error) => {
            if (error.ParamName) {
              if (form.controls[error.ParamName]) {
                form.controls[error.ParamName].setErrors({ custom: true });
                var errorMessage = error.Message.split("Parameter name")[0];
                this.errorMessages[error.ParamName] = errorMessage;
              }
            }
            this.isSavingLocation = false;
          }
        );
    } else {
      this.locationService
        .post<LocationViewModel>(this.locationViewModel)
        .subscribe(
          (result) => {
            this.messageService.create("success", "Lokacija uspješno dodana");
            this.isSavingLocation = false;
            this.searchData();
            form.reset();
            this.closeEditDrawer();
          },
          (error) => {
            if (error.ParamName) {
              if (form.controls[error.ParamName]) {
                form.controls[error.ParamName].setErrors({ custom: true });
                var errorMessage = error.Message.split("Parameter name")[0];
                this.errorMessages[error.ParamName] = errorMessage;
              }
            }
            this.isSavingLocation = false;
          }
        );
    }
  }

  editLocation(location: Location) {
    this.initModel(location);
    this.isEditing = true;
    this.openEditDrawer();
  }

  deleteLocation(id: number) {
    this.locationService.delete(id).subscribe(
      (result) => {
        this.messageService.create("success", "Lokacija uspješno obrisana");
        this.searchData();
      },
      (error) => {
        console.log(error);
      }
    );
  }

  loadDataForDropdowns() {
    this.employeeService.getAllBasic().subscribe((data: Employee[]) => {
      this.employees = data;
    });
    this.legalEntityService.getAllBasic().subscribe((data: LegalEntity[]) => {
      this.legalEntities = data;
    });
  }

  searchData(reset: boolean = false): void {
    if (reset) {
      this.pageIndex = 1;
    }
    var filters = "";
    if (this.nameSearchValue.length > 0) {
      filters += "Name@=" + this.nameSearchValue + ",";
    }
    if (this.employeeIdSearchValue.length > 0) {
      filters +=
        "PersonInChargeId==" + this.employeeIdSearchValue.join("|") + ",";
    }
    if (this.legalEntityIdSearchValue.length > 0) {
      filters +=
        "LegalEntityId==" +
        this.legalEntityIdSearchValue.join("|") +
        ",";
    }
    if (this.typeSearchValue.length > 0) {
      filters += "Type==" + this.typeSearchValue.join("|") + ",";
    }
    this.loading = true;
    this.locationService
      .getPage(this.pageIndex, this.pageSize, "-Created", filters)
      .subscribe((data: ITuple<Location>) => {
        this.loading = false;
        this.total = data.item2;
        this.locationList = data.item1;
      });
  }

  reset(): void {
    this.nameSearchValue = "";
    this.employeeIdSearchValue = [];
    this.typeSearchValue = [];
    this.legalEntityIdSearchValue = [];
    this.searchData(true);
  }

  // onLocationInput(value: string): void {
  //   this.filteredLocations = this.locations.filter(location => location.name.toLowerCase().includes(value.toLowerCase()));
  // }

  openEditDrawer() {
    this.editDrawerVisible = true;
  }

  closeEditDrawer(form?: NgForm) {
    this.isEditing = false;
    this.editDrawerVisible = false;
    if (form) {
      form.reset();
    }
    this.initModel();
  }

  openDetails(location: Location) {
    this.locationService.get(location.id).subscribe((data: Location) => {
      this.selectedLocation = data;
      this.assetService
        .getPage(1, 10, "-Created", `LocationId==${location.id}`)
        .subscribe((data: ITuple<Asset>) => {
          this.selectedLocationAssetsSubject.next(data);
        });
    });
  }

  openEmployeeDetails(employee: Employee) {
    this.employeeService.get(employee.id).subscribe((data: Employee) => {
      this.selectedEmployee = data;
    });
  }

  closeEmployeeDetails() {
    this.selectedEmployee = null;
  }

  closeDetails() {
    this.selectedLocation = null;
  }

  public getLocationTypeName(locationType: any) {
    var name = LocationTypeName.get(locationType);
    return name;
  }

  public locationTypeKeys() {
    var keys = Object.keys(this.locationTypes);
    return keys.slice(keys.length / 2);
  }
}
